/**
 * The end of every drill-down: one voucher, its lines, and the only
 * correction path -- reversal.
 *
 * There is deliberately no Edit button. A posted voucher is immutable;
 * "altering" it means reversing and re-posting, which leaves an audit
 * trail. This is stricter than Tally and is the groundwork for ZATCA,
 * which forbids modifying an issued document.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Drawer, Skeleton, Stack, TextField, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { fetchVoucher, reverseVoucher } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { c, fmtMoney } from '../theme'

interface Props {
  voucherId: number | null
  onClose: () => void
  onLedgerClick?: (code: string) => void
}

export default function VoucherDrawer({ voucherId, onClose, onLedgerClick }: Props) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const qc = useQueryClient()
  const [confirm, setConfirm] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const q = useQuery({
    enabled: voucherId != null,
    queryKey: ['voucher', voucherId],
    queryFn: () => fetchVoucher(voucherId!),
  })

  const reverse = useMutation({
    mutationFn: () => reverseVoucher(voucherId!, reason),
    onSuccess: () => {
      setConfirm(false); setReason('')
      void qc.invalidateQueries({ queryKey: ['daybook'] })
      void qc.invalidateQueries({ queryKey: ['ledger'] })
      void qc.invalidateQueries({ queryKey: ['trial-balance'] })
      void qc.invalidateQueries({ queryKey: ['balance-sheet'] })
      void qc.invalidateQueries({ queryKey: ['voucher', voucherId] })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const v = q.data
  const reversed = v?.status === 'REVERSED'

  return (
    <>
      <Drawer anchor="right" open={voucherId != null} onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 460 } } }}>
        <Box sx={{ p: 2.5 }}>
          {q.isLoading ? (
            <><Skeleton height={32} /><Skeleton /><Skeleton /></>
          ) : v ? (
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Typography className="num" sx={{ fontSize: 16, fontWeight: 600 }}>
                  {v.voucher_no}
                </Typography>
                {reversed && (
                  <Chip size="small" label={t('report.reversed')}
                    sx={{ bgcolor: c.dangerSoft, color: c.dangerInk }} />
                )}
              </Stack>

              <Stack direction="row" spacing={3}>
                <Box>
                  <Typography className="section-label">{t('entry.date')}</Typography>
                  <Typography className="num" sx={{ fontSize: 13 }}>
                    {v.voucher_date}</Typography>
                </Box>
                <Box>
                  <Typography className="section-label">{t('report.type')}</Typography>
                  <Typography sx={{ fontSize: 13 }}>{v.voucher_kind}</Typography>
                </Box>
              </Stack>

              {v.narration && (
                <Box>
                  <Typography className="section-label">
                    {t('entry.narration')}</Typography>
                  <Typography sx={{ fontSize: 13.5 }}>{v.narration}</Typography>
                </Box>
              )}

              <Divider />

              <Box className="ruled">
                {v.lines.map((l) => (
                  <Stack key={l.line_no} direction="row" spacing={1}
                    alignItems="baseline"
                    onClick={() => onLedgerClick?.(l.ledger_code)}
                    sx={{
                      py: 1, cursor: onLedgerClick ? 'pointer' : 'default',
                      '&:hover': { bgcolor: onLedgerClick ? c.primarySoft : undefined },
                    }}>
                    <Typography sx={{
                      fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600,
                      width: 24, color: c.inkMuted,
                    }}>{l.dr_cr === 'D' ? 'Dr' : 'Cr'}</Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5 }} noWrap>
                        {l.ledger_name}</Typography>
                      <Typography className="num" sx={{
                        fontSize: 11, color: c.inkMuted, textAlign: 'start' }}>
                        {l.ledger_code}</Typography>
                    </Box>
                    <span className="num" style={{ fontSize: 13.5 }}>
                      {fmtMoney(l.amount)}</span>
                  </Stack>
                ))}
              </Box>

              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography className="section-label">{t('report.total')}</Typography>
                <span className="num" style={{ fontWeight: 600 }}>
                  {fmtMoney(v.total)}</span>
              </Stack>

              {v.reverses_voucher_id && (
                <Alert severity="info" sx={{ fontSize: 13 }}>
                  {t('report.isReversalOf')}
                </Alert>
              )}

              {error && <Alert severity="error" onClose={() => setError(null)}>
                {error}</Alert>}

              {can(P.reverse) && !reversed && !v.reverses_voucher_id && (
                <Button variant="outlined" color="error"
                  onClick={() => setConfirm(true)}>
                  {t('report.reverse')}
                </Button>
              )}

              <Typography variant="caption" color="text.secondary">
                {t('report.immutableNote')}
              </Typography>
            </Stack>
          ) : null}
        </Box>
      </Drawer>

      <Dialog open={confirm} onClose={() => setConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('report.reverse')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('report.reverseExplain')}
          </Typography>
          <TextField autoFocus fullWidth label={t('report.reason')} value={reason}
            onChange={(e) => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirm(false)}>
            {t('common.cancel')}</Button>
          <Button variant="contained" color="error"
            disabled={reason.trim().length < 3 || reverse.isPending}
            onClick={() => reverse.mutate()}>
            {t('report.confirmReverse')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
